<template>
  <v-container fluid grid-list-sm>
    <v-card>
      <v-toolbar :dense="true">
        <v-toolbar-title><v-icon>mdi-attachment</v-icon> Attachments</v-toolbar-title>
        <v-spacer />
        <span class="body-2">Claim No: {{ this.commonInfo.clmNo }}</span>
      </v-toolbar>
      <v-card-text>
        <v-layout row wrap>
          <form ref="clmFileForm">
            <input type="file" style="display: none" ref="clmFile" multiple @change="doValidateFile" />
          </form>
          <v-text-field
            v-model="filename"
            :label="label.fileInput"
            readonly
            @click.prevent="
              $refs.clmFileForm.reset()
              $refs.clmFile.click()
            "
            chips
            multiple
            prepend-icon="mdi-file-document"
            :error-messages="fileErrors"
          ></v-text-field>
          <v-btn color="primary" @click="doSave()">
            Attach
            <v-icon right>mdi-plus</v-icon>
          </v-btn>
        </v-layout>
        <v-layout row wrap>
          <v-flex>
            <v-data-table :headers="attachColumns" :items="attachmentList" class="elevation-1" :loading="loading">
              <template v-slot:item="row"
                ><tr>
                  <td>{{ row.index + 1 }}</td>
                  <td>{{ row.item.clmAttachFileNm }}</td>
                  <td class="table-action-header">
                    <table-action
                      :rowData="row"
                      :query="checkAccess([permissionKeys.perm_031065])"
                      @query="doQuery(row.item)"
                      :remove="checkAccess([permissionKeys.perm_031070])"
                      @remove="doRemove(row.item)"
                      :custom="checkAccess([permissionKeys.perm_031085])"
                      customIcon="mdi-download"
                      customText="Download"
                      @custom="doDownload(row.item)"
                    ></table-action>
                  </td></tr
              ></template>
            </v-data-table>
          </v-flex>
        </v-layout>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="red darken-1" text @click.native="doClose">Close</v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog v-model="showClmAttach" width="600px">
      <v-card>
        <v-card-title>
          <span class="headline"> Query File Attachments - {{ this.commonInfo.clmNo }}</span>
          <v-spacer></v-spacer>
          <v-btn text icon @click="showClmAttach = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-layout row>
            <v-flex xs6>
              <v-subheader class="queryLabel">Claim Attach Id</v-subheader>
              <div class="queryValue">{{ attachmentInfo.clmAttachId }}</div>
            </v-flex>
            <v-flex xs6>
              <v-subheader class="queryLabel">Claim Attach File Name</v-subheader>
              <div class="queryValue">{{ attachmentInfo.clmAttachFileNm }}</div>
            </v-flex>
          </v-layout>
          <v-divider></v-divider>
          <v-layout row>
            <v-flex xs6>
              <v-subheader class="queryLabel">Created By</v-subheader>
              <div class="queryValue">{{ attachmentInfo.createdBy }}</div>
            </v-flex>
            <v-flex xs6>
              <v-subheader class="queryLabel">Created Date</v-subheader>
              <div class="queryValue">{{ formatDisplayDate(attachmentInfo.createdDate) }}</div>
            </v-flex>
          </v-layout>
          <v-layout row>
            <v-flex xs6>
              <v-subheader class="queryLabel">Modified By</v-subheader>
              <div class="queryValue">{{ attachmentInfo.modifiedBy }}</div>
            </v-flex>
            <v-flex xs6>
              <v-subheader class="queryLabel">Modified Date</v-subheader>
              <div class="queryValue">{{ formatDisplayDate(attachmentInfo.modifiedDate) }}</div>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>
    </v-dialog>

    <confirm ref="confirmDelete">
      <p>Are you sure you want to delete the attachment?</p>
    </confirm>
  </v-container>
</template>

<script>
import MessageMixin from '@/mixins/message'
import * as TableConst from '@/mixins/table'
import { expandURI } from '@/utils/ajax'
import { extractMenu } from '@/utils/extract'
import { formatDisplayDate } from '@/utils/date'
import { downloadPrompt } from '@/utils/file'
import { mapGetters } from 'vuex'
import { permissionKeys } from '@/mixins/global/permissionKey'
export default {
  props: {},
  data() {
    return {
      attachmentList: [],
      label: {
        fileInput: 'File Input'
      },
      attachColumns: [
        { text: 'S/N', align: 'left', sortable: false },
        { text: 'File Name', align: 'left', sortable: false },
        TableConst.tableActionHeader
      ],
      attachmentInfo: {
        clmAttachId: '',
        clmAttachFileNm: '',
        createdBy: '',
        createdDate: '',
        modifiedBy: '',
        modifiedDate: ''
      },
      file: undefined,
      filename: '',
      showClmAttach: false,
      fileErrors: [],
      rejectList: ['bat', 'com', 'exe', 'js'],
      permissionKeys: permissionKeys
    }
  },
  computed: {
    commonInfo() {
      return this.$store.state.claims.claimDetail.commonInfo
    },
    ...mapGetters(['checkAccess'])
  },
  watch: {},
  methods: {
    async doFetch() {
      this.loading = true
      const apiUrl = this.$store.state.claims.claimDetail.menu.clmAttachs
      if (apiUrl) {
        this.loading = true
        const expand = {
          clmNo: this.commonInfo.clmNo
        }
        const result = await this.$http.get(expandURI(apiUrl, expand))
        if (result && result.data) {
          const data = result.data
          this.menu = extractMenu(result.data._links)
          this.attachmentList = data.data.clmAttachs
        } else {
          this.$toast.error('Unexpected server response')
        }
      } else {
        this.$toast.error('Missing API URL (fetch claim attachments)')
      }
      this.loading = false
    },
    doValidateFile(e) {
      this.clear()
      this.resetMessages()
      if (e.target.files[0]) {
        const file = e.target.files[0]
        this.filename = file.name
        if (file.size > 1048576) {
          this.fileErrors = 'File size should be less than 10 MB'
        } else {
          const splitName = file.name.split('.')
          const length = splitName.length
          if (length > 1 && this.rejectList.indexOf(splitName[length - 1].toLowerCase()) > -1) {
            this.fileErrors = 'Files with the following extensions (i.e. js, exe, bat, com) are not allowed.'
          } else {
            this.file = file
          }
        }
      }
    },
    async doSave() {
      if (!this.filename) {
        this.fileErrors = 'Please select file to attach'
        return
      }
      if (!this.file) {
        return
      }
      this.loading = true
      const apiUrl = this.menu.claimAttachSave
      if (apiUrl) {
        try {
          let formData = new FormData()
          formData.append('file', this.file)
          formData.append('filename', this.filename)
          formData.append('clmNo', this.commonInfo.clmNo)
          const result = await this.$http.post(apiUrl, formData)
          if (result && result.data) {
            this.mode = 'list'
            this.doFetch()
            this.notify(result.data)
          } else {
            this.$toast.error('Unexpected server response')
          }
        } catch (err) {
          if (err.response && err.response.data) {
            this.notify(err.response.data)
          } else {
            this.$toast.error('Failed to save claim attachment')
          }
        }
      } else {
        this.$toast.error('missing apiUrl (claimhandlers)')
      }
      this.clear()
    },
    async doRemove(clmAttach) {
      const prompt = await this.$refs.confirmDelete.doPopUp()
      if (!prompt) return
      const apiURI = this.menu.claimAttachRemove
      if (clmAttach && apiURI) {
        const expand = {
          clmAttachId: clmAttach.clmAttachId
        }
        try {
          const result = await this.$http.delete(expandURI(apiURI, expand))
          const data = result.data
          this.doFetch()
          this.notify(data)
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    async doQuery(clmAttach) {
      const apiURI = this.$store.state.claims.claimDetail.menu.queryClmAttachs
      if (apiURI) {
        const expand = {
          clmAttachId: clmAttach.clmAttachId
        }
        try {
          const result = await this.$http.get(expandURI(apiURI, expand))
          const { data } = result.data
          if (data) {
            this.attachmentInfo.clmAttachId = data.clmAttachs.clmAttachId
            this.attachmentInfo.clmAttachFileNm = data.clmAttachs.clmAttachFileNm
            this.attachmentInfo.createdBy = data.createdBy
            this.attachmentInfo.createdDate = data.createdDate
            this.attachmentInfo.modifiedBy = data.modifiedBy !== null ? data.modifiedBy : '-'
            this.attachmentInfo.modifiedDate = data.modifiedDate !== null ? data.modifiedDate : '-'
          }
          this.notify(data)
          this.showClmAttach = true
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    async doDownload(job) {
      const apiURI = this.$store.state.claims.claimDetail.menu.downloadClaimAttachs
      if (apiURI) {
        try {
          const expand = {
            clmNo: this.commonInfo.clmNo
          }
          const result = await this.$http.get(expandURI(apiURI, expand), {
            responseType: 'blob'
          })
          var headers = result.headers
          var blob = new Blob([result.data], { type: headers['content-type'] })
          var filename = (headers['content-disposition'] || 'file').split('filename=')[1]
          downloadPrompt(blob, filename)
        } catch (err) {
          const { data } = err.response
          if (data) {
            // As the request is requesting blob, error has to be formatted back to json
            try {
              const errUrl = URL.createObjectURL(data)
              const errRes = await this.$http.get(errUrl)
              URL.revokeObjectURL(errUrl)
              if (errRes.data) {
                this.notify(errRes.data)
              }
            } catch (err2) {
              throw err2
            }
          }
        }
      } else {
        this.notify('The request failed because it is trying to execute an invalid API url.')
      }
    },
    clear() {
      this.file = undefined
      this.filename = ''
      this.fileErrors = []
    },
    doClose() {
      this.clear()
      this.$emit('close')
    },
    formatDisplayDate(xDate) {
      return formatDisplayDate(xDate)
    }
  },
  mounted() {
    this.doFetch()
  },
  mixins: [MessageMixin, TableConst.default]
}
</script>
